import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";

import {
  IIntegrationApiKey,
  IIntegrationApiKeyClaim,
  TIntegrationApiKeyRegion,
} from "../../../@types/admin/AdminIntegrationApiKey";
import {
  useCreateIntegrationApiKey,
  useUpdateIntegrationApiKey,
} from "../../../hooks/mutations/admin/integrationApiKeys";
import { useIntegrationApiKeyClaims } from "../../../hooks/queries/admin/integrationApiKeys";
import { useModal } from "../../../hooks/useModal";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage, HttpError } from "../../../utils/HttpError";
import {
  AdminDateField,
  AdminSelectField,
  CheckboxField,
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalHeader,
  ModalSubtitle,
  ModalTitle,
  WarningNotice,
} from "../shared";
import {
  ClaimDescription,
  ClaimsList,
  CopyKeyButton,
  KeyPanel,
  KeyRow,
  KeyValue,
} from "./AdminIntegrationApiKeys.styles";

const DATE_VALUE = "YYYY-MM-DD";
const REGIONS: TIntegrationApiKeyRegion[] = ["EU", "US"];

/** Where each server `propertyName` lands in the form. */
const PROPERTY_TO_FIELD: Record<string, TFieldName> = {
  description: "description",
  region: "region",
  claims: "claims",
  expiresin: "expiresIn",
};

type TFieldName = "description" | "region" | "claims" | "expiresIn";

interface IIntegrationApiKeyModalProps {
  /** Omitted for a create. */
  apiKey?: IIntegrationApiKey;
  onClose: () => void;
}

/** Help text for a claim the key holds that the catalogue does not list. */
export const UNCATALOGUED_CLAIM_HINT = "Not in the catalogue; kept as-is";

/**
 * The checklist to render: the catalogue, then any claim the key already has
 * that the catalogue does not know. The Global table is shared with the
 * integration API, so existing keys carry claims this page never minted
 * (PATIENT_OUTBOUND_INTEGRATION, AIOCARE_SYNC, …). Hiding those would drop
 * them from the next PUT without anyone choosing to — so they are shown,
 * checked, and only leave when unticked.
 */
export function claimChecklist(
  catalogue: IIntegrationApiKeyClaim[],
  current: string[]
): IIntegrationApiKeyClaim[] {
  const known = new Set(catalogue.map((claim) => claim.code));
  const extras = current
    .filter((code) => !known.has(code))
    .map((code) => ({ code, description: UNCATALOGUED_CLAIM_HINT }));
  return [...catalogue, ...extras];
}

/** A date input value → the ISO instant the API stores (end of that day, local). */
export function toExpiresIn(date: string): string {
  return dayjs(date, DATE_VALUE).endOf("day").toISOString();
}

/** Default expiry for a new key: one year from today. */
export function defaultExpiryDate(): string {
  return dayjs().add(1, "year").format(DATE_VALUE);
}

/**
 * Create / edit an integration API key.
 *
 * Create hands back the plain key exactly once, so on success the form is
 * replaced by a copy panel and the modal cannot fall back to the form: there
 * is nothing to re-submit, and re-creating would mint a second key. The value
 * lives only in component state and is never toasted or logged. While it is
 * shown the modal ignores Escape and outside clicks, and Close stays disabled
 * until the operator confirms the key was stored; closing also clears the
 * create mutation so the value does not linger in the query cache.
 */
export function IntegrationApiKeyModal({
  apiKey,
  onClose,
}: IIntegrationApiKeyModalProps) {
  const isEdit = Boolean(apiKey);
  const { showToast } = useToast();
  const { claims: claimOptions, isLoading: claimsLoading } =
    useIntegrationApiKeyClaims();
  const { updateModalOptions } = useModal();
  const { createApiKey, isCreatingApiKey, resetCreateApiKey } =
    useCreateIntegrationApiKey();
  const { updateApiKey, isUpdatingApiKey } = useUpdateIntegrationApiKey();

  const [description, setDescription] = useState(apiKey?.description ?? "");
  const [region, setRegion] = useState<TIntegrationApiKeyRegion | "">(
    apiKey?.region ?? ""
  );
  const [claims, setClaims] = useState<string[]>(apiKey?.claims ?? []);
  const initialExpiryDate = apiKey
    ? dayjs(apiKey.expiresIn).format(DATE_VALUE)
    : "";
  const [expiresIn, setExpiresIn] = useState(
    apiKey ? initialExpiryDate : defaultExpiryDate()
  );
  const [active, setActive] = useState(apiKey?.active ?? true);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [serverErrors, setServerErrors] = useState<
    Partial<Record<TFieldName, string>>
  >({});
  const [formErrors, setFormErrors] = useState<string[]>([]);

  /** Set once the create succeeded; cleared only by the explicit Close. */
  const [plainKey, setPlainKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmedStored, setConfirmedStored] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    },
    []
  );

  const isSaving = isCreatingApiKey || isUpdatingApiKey;

  const touch = (field: string) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const errors: Partial<Record<TFieldName, string>> = {
    description: description.trim() ? undefined : "Description is required.",
    region: isEdit || region ? undefined : "Select a region.",
    claims: claims.length > 0 ? undefined : "Select at least one claim.",
    expiresIn: (() => {
      if (!expiresIn) return "Expiry date is required.";
      if (!isEdit && !dayjs(expiresIn, DATE_VALUE).isAfter(dayjs(), "day")) {
        return "Expiry must be after today.";
      }
      return undefined;
    })(),
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const shownError = (field: TFieldName) =>
    serverErrors[field] ??
    (isEdit || touched[field] ? errors[field] : undefined);

  const toggleClaim = (code: string) => {
    touch("claims");
    setServerErrors((prev) => ({ ...prev, claims: undefined }));
    setClaims((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const applyServerErrors = (error: unknown): boolean => {
    if (!(error instanceof HttpError) || error.status !== 400) return false;
    const mapped: Partial<Record<TFieldName, string>> = {};
    const unmapped: string[] = [];
    error.errors.forEach((e) => {
      const field = e.propertyName
        ? PROPERTY_TO_FIELD[e.propertyName.toLowerCase()]
        : undefined;
      if (field) mapped[field] = e.message;
      else if (e.message) unmapped.push(e.message);
    });
    if (Object.keys(mapped).length === 0 && unmapped.length === 0) return false;
    setServerErrors(mapped);
    setFormErrors(unmapped);
    return true;
  };

  const handleSave = async () => {
    if (hasErrors) return;
    setServerErrors({});
    setFormErrors([]);

    try {
      if (apiKey) {
        await updateApiKey({
          id: apiKey.id,
          request: {
            description: description.trim(),
            claims,
            // Only a changed date is turned into end-of-day: re-sending the
            // stored instant keeps an unrelated edit from moving the expiry.
            expiresIn:
              expiresIn === initialExpiryDate
                ? apiKey.expiresIn
                : toExpiresIn(expiresIn),
            active,
          },
        });
        showToast("The API key was updated successfully.", "success");
        onClose();
        return;
      }

      const created = await createApiKey({
        description: description.trim(),
        region: region as TIntegrationApiKeyRegion,
        claims,
        expiresIn: toExpiresIn(expiresIn),
      });
      // Deliberately no toast with the value — it must exist on screen only.
      setPlainKey(created.plainKey);
      // The key cannot be shown again: only the explicit Close may dismiss it.
      updateModalOptions?.({ dismissible: false, showCloseButton: false });
    } catch (error) {
      if (applyServerErrors(error)) return;
      showToast(
        errorToastMessage(
          error,
          isEdit
            ? "There was an error updating this API key."
            : "There was an error creating this API key."
        ),
        "warning"
      );
    }
  };

  const handleCopy = async () => {
    if (!plainKey) return;
    try {
      await navigator.clipboard?.writeText(plainKey);
      setCopied(true);
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const closeCreated = () => {
    setPlainKey(null);
    resetCreateApiKey();
    updateModalOptions?.({ dismissible: true });
    onClose();
  };

  if (plainKey) {
    return (
      <ModalContainer data-testid="api-key-created">
        <ModalHeader>
          <ModalTitle>Copy your key now</ModalTitle>
          <ModalSubtitle>
            {description.trim()} · {region}
          </ModalSubtitle>
        </ModalHeader>
        <KeyPanel>
          <KeyRow>
            <KeyValue
              readOnly
              value={plainKey}
              aria-label="API key"
              onFocus={(e) => e.currentTarget.select()}
            />
            <CopyKeyButton
              type="button"
              copied={copied}
              aria-label="Copy API key"
              onClick={handleCopy}
            >
              {copied ? "Copied" : "Copy"}
            </CopyKeyButton>
          </KeyRow>
          <WarningNotice>
            This is the only time the key is shown. Store it in the
            consumer&apos;s secret store (e.g. the integration API&apos;s
            InternalApi settings) before closing.
          </WarningNotice>
          <CheckboxField>
            <input
              type="checkbox"
              checked={confirmedStored}
              onChange={(e) => setConfirmedStored(e.target.checked)}
              data-testid="api-key-created-confirm"
            />
            I have copied the key and stored it safely.
          </CheckboxField>
        </KeyPanel>
        <ModalFormActions>
          <FilterButton
            variant="primary"
            disabled={!confirmedStored}
            onClick={closeCreated}
            data-testid="api-key-created-close"
          >
            Close
          </FilterButton>
        </ModalFormActions>
      </ModalContainer>
    );
  }

  return (
    <ModalContainer data-testid="api-key-modal">
      <ModalHeader>
        <ModalTitle>{isEdit ? "Edit API key" : "New API key"}</ModalTitle>
        {isEdit && apiKey?.keyPrefix ? (
          <ModalSubtitle>
            Prefix {apiKey.keyPrefix}… · {apiKey.region}. The region and the key
            value cannot be changed.
          </ModalSubtitle>
        ) : null}
      </ModalHeader>

      <ModalForm>
        <FilterGroup>
          <FilterLabel htmlFor="api-key-description">Description</FilterLabel>
          <FilterInput
            id="api-key-description"
            type="text"
            maxLength={255}
            value={description}
            onBlur={() => touch("description")}
            onChange={(e) => {
              touch("description");
              setServerErrors((prev) => ({ ...prev, description: undefined }));
              setDescription(e.target.value);
            }}
          />
          <FieldHint>
            Who or what uses this key, e.g. &quot;TMP webhook
            (production)&quot;.
          </FieldHint>
          {shownError("description") && (
            <FieldError>{shownError("description")}</FieldError>
          )}
        </FilterGroup>

        {!isEdit && (
          <FilterGroup>
            <FilterLabel htmlFor="api-key-region">Region</FilterLabel>
            <AdminSelectField
              id="api-key-region"
              value={region}
              onBlur={() => touch("region")}
              onChange={(e) => {
                touch("region");
                setServerErrors((prev) => ({ ...prev, region: undefined }));
                setRegion(e.target.value as TIntegrationApiKeyRegion | "");
              }}
            >
              <option value="">Select a region</option>
              {REGIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </AdminSelectField>
            <FieldHint>
              The API the key authenticates against. Cannot be changed later.
            </FieldHint>
            {shownError("region") && (
              <FieldError>{shownError("region")}</FieldError>
            )}
          </FilterGroup>
        )}

        <FilterGroup>
          <FilterLabel>Claims</FilterLabel>
          {claimsLoading ? (
            <FieldHint>Loading claims...</FieldHint>
          ) : (
            <ClaimsList data-testid="api-key-claims">
              {claimChecklist(claimOptions, apiKey?.claims ?? []).map(
                (claim) => (
                  <div key={claim.code}>
                    <CheckboxField>
                      <input
                        type="checkbox"
                        checked={claims.includes(claim.code)}
                        onChange={() => toggleClaim(claim.code)}
                      />
                      {claim.code}
                    </CheckboxField>
                    {claim.description && (
                      <ClaimDescription>{claim.description}</ClaimDescription>
                    )}
                  </div>
                )
              )}
            </ClaimsList>
          )}
          {shownError("claims") && (
            <FieldError>{shownError("claims")}</FieldError>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="api-key-expires">Expires</FilterLabel>
          <AdminDateField
            id="api-key-expires"
            value={expiresIn}
            onChange={(next) => {
              touch("expiresIn");
              setServerErrors((prev) => ({ ...prev, expiresIn: undefined }));
              setExpiresIn(next);
            }}
          />
          <FieldHint>The key stops working at the end of this day.</FieldHint>
          {shownError("expiresIn") && (
            <FieldError>{shownError("expiresIn")}</FieldError>
          )}
        </FilterGroup>

        {isEdit && (
          <FilterGroup>
            <FilterLabel>State</FilterLabel>
            <CheckboxField>
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
              Active
            </CheckboxField>
            <FieldHint>
              Unticking revokes the key; every consumer using it is locked out
              on save.
            </FieldHint>
          </FilterGroup>
        )}

        {formErrors.length > 0 && (
          <WarningNotice data-testid="api-key-form-error">
            {formErrors.join(" ")}
          </WarningNotice>
        )}

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isSaving}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isSaving || hasErrors}
            onClick={handleSave}
            data-testid="api-key-save"
          >
            {(() => {
              if (isSaving) return isEdit ? "Saving..." : "Creating...";
              return isEdit ? "Save" : "Create key";
            })()}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
